import React from 'react';
import styled, { CSSProperties, useTheme } from 'styled-components/macro';
import { useField } from 'formik';
import { FormikErrorMessage } from './FormikErrorMessage';
import { FormikLabel } from './FormikLabel';
import { Flex, FlexProps } from '../styles/layout';
import { MdWarning } from 'react-icons/md';
import { Typography } from 'react-covideo-common';

const Textarea = styled.textarea`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  width: 100%;
  height: 100px;
  padding: 8px 12px;
  min-height: 100px;
  max-height: 300px;
  border-radius: 4px;
  box-sizing: border-box;
  border: solid 1px #d0d3d9;
  resize: vertical;
  &:focus {
    outline: none;
    border: solid 1px ${({ theme }) => theme.colors.neutral[40]};
  }
`;

const CharCounter = styled.span<{ isMaxLengthReached?: boolean }>`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 12px;
  right: 12px;
  pointer-events: none;

  padding: 0 4px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(2px);
  border-radius: 4px;

  z-index: 2;

  font-size: 12px;
  line-height: 24px;
  color: ${({ isMaxLengthReached, theme }) =>
    theme.colors[isMaxLengthReached ? 'danger' : 'neutral'][100]};
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

interface CustomTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: string;
  label: string;
  isRequired: boolean;
  showCounter?: boolean;
  maxLength?: number;
  extendStyles?: {
    errorWrapper?: CSSProperties;
    labelStyles?: CSSProperties;
    inputWrapper?: FlexProps;
    mainWrapper?: FlexProps;
    textAreaStyles?: CSSProperties;
  };
}

export const FormikTextAreaField = ({
  label,
  isRequired,
  extendStyles,
  showCounter = false,
  maxLength,
  ...props
}: CustomTextareaProps) => {
  const [field, meta, helpers] = useField(props);
  const { setTouched } = helpers;
  const theme = useTheme();
  const isMaxLengthReached =
    maxLength !== undefined && String(field.value).length >= maxLength;

  const handleFocus = () => {
    setTouched(true, false);
  };

  return (
    <Flex {...extendStyles?.mainWrapper} gap='4px'>
      <FormikLabel
        label={label}
        name={props.name}
        isRequired={isRequired}
        disabled={!!props.disabled}
        labelStyles={extendStyles?.labelStyles}
      />
      <Flex {...extendStyles?.inputWrapper} gap='4px'>
        <Wrapper>
          <Textarea
            style={extendStyles?.textAreaStyles}
            {...field}
            {...props}
            id={props.name}
            onFocus={handleFocus}
          />
          {showCounter && maxLength && (
            <CharCounter isMaxLengthReached={isMaxLengthReached}>
              {isMaxLengthReached && (
                <MdWarning
                  color={theme.colors.danger[100]}
                  size={14}
                  style={{ marginRight: '0px' }}
                />
              )}
              <Typography variant='extra-small'>
                {String(field.value).length}/{maxLength}
              </Typography>
            </CharCounter>
          )}
        </Wrapper>
        <FormikErrorMessage meta={meta} extendStyles={extendStyles} />
      </Flex>
    </Flex>
  );
};
